/**
 * Extended entity property change application — handles property
 * mutations for extended entity types (Dimension, MText, Multileader, etc.)
 *
 * Extracted from simpleGeometry.ts — pure structural refactor.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
// Primitive helpers + types live in `geometryEntityTypes.ts` so this
// module can import them without re-importing simpleGeometry (which
// closed a depcruise cycle simpleGeometry.ts ↔ extendedPropertyMutations.ts).
import {
  parseNumber,
  formatNumber,
  parseClosedFlagProperty,
  type ExtendedGeometryPatch,
  type SimpleGeometryPropertyChange,
  type SimpleGeometryPropertySkip,
} from "./geometryEntityTypes";
import {
  computeDimensionMeasurement,
  extendedPatchableLookup,
  type ExtendedPropertyChangeResult,
} from "./previewBuilders";
// fix/properties-apply-derived-fields-skip — ONE-TRUTH classification (typed
// CAD-core registry) distinguishing a KNOWN computed/display-only field
// (LWPOLYLINE Length/Area, Dimension Measurement, Multileader Leader count,
// … — `read-only-calc` descriptors) from a genuine incompatibility. This
// module already imports the typed writer mirror below
// (`typedPropertyWriter.ts` → `typedPropertyDescriptors.ts`), so this direct
// import adds no new dep-graph edge class (ПРИОРИТЕТ #2).
import { isDerivedDisplayProperty } from "./typedPropertyDescriptors";
// Codex P2 (#417) — ONE TRUTH for numeric classification + grammar, shared with
// the apps/web document translator (`propertyTranslator.ts`) so the SHELL and
// the DOCUMENT accept/reject the EXACT same numeric values for every mapped
// numeric field (bounded AND unbounded). The shell works with property LABELS,
// so it asks `classifyNumericLabel` (label → numeric field + range bound) and
// grades the string with the same `parseFiniteNumeric` + `satisfiesNumeric-
// Constraint` the document uses.
import {
  classifyEnumLabel,
  classifyNumericLabel,
  classifyStringDomainLabel,
  parseFiniteNumeric,
  satisfiesNumericConstraint,
} from "./numericFieldClassification";
// S15 Slice A (@codex P2 ×2 on c8394b70) — the typed writer's OWN accept/reject
// verdict for a writer-OWNED extended-geometry label (ArrayRect Rows/Columns
// min:1+int, LWPOLYLINE Constant width min:0, malformed serialized lists, …),
// mirrored here so the SHELL preview can never keep a value the DOCUMENT write
// (geometryReducer Step 1d) rejected. One truth: the mirror resolves the same
// registry descriptor and runs the same `parseTypedPropertyInput` core gate the
// writer itself uses — see `typedPropertyWriter.ts`. (This import is legal
// cycle-wise because `propertyCapabilities` now imports the preview-property
// lists from their defining `previewBuilders` module instead of the
// `simpleGeometry` barrel that re-exports THIS file.)
import {
  typedExtendedGeometryShellCanonicalizes,
  typedExtendedGeometryShellRejects,
} from "./typedPropertyWriter";

/**
 * Codex P2-5 (#317) — ALL-OR-NOTHING validation for the coordinate / knot
 * string properties so the SHELL preview agrees with the DOCUMENT translator.
 *
 * The document translator (`propertyTranslator.translateExtendedEntityPropertyChanges`)
 * rejects a malformed `Vertices` / `Control points` / `Fit points` / `Knots`
 * edit (omits the document patch). If the SHELL reducer still copied the bad
 * value into the preview, the no-op resync fast path kept the mutated preview
 * → the UI showed the bad edit while the document / DXF export rejected it
 * (divergence). Rejecting the SAME malformed values HERE keeps both sides in
 * lockstep: neither shell nor document changes.
 *
 * Returns true when `value` is a well-formed value for `property`, false to
 * reject the WHOLE edit for that key (a single bad pair / non-finite knot
 * fails the entire string — never truncate to a valid subset).
 */
/**
 * Codex P2-1 (#317·round-6) — count the entity's vertices from its preview
 * `Vertices` property so the SHELL `Bulges` gate can count-lock against the
 * SAME vertex count the DOCUMENT translator uses (`docVerts.length`).
 *
 * The preview `Vertices` value is EITHER a coordinate string
 * (`"x,y;x,y;…"` — the app's `dxfGeometry.buildLwpolylinePreview` + grip
 * drag) OR a bare integer count (command-engine's own preview builder).
 * Returns the segment / count value, or `undefined` when it cannot be
 * determined (no `Vertices` property threaded) — the caller then falls back
 * to the parsed token count, mirroring the document side's fallback.
 */
function lwpolylineVertexCount(
  verticesValue: string | undefined,
): number | undefined {
  if (verticesValue === undefined) return undefined;
  const trimmed = verticesValue.trim();
  if (trimmed === "") return undefined;
  // Bare integer count form (command-engine preview builder).
  if (!trimmed.includes(";") && !trimmed.includes(",")) {
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 ? n : undefined;
  }
  // Coordinate-pair string form — count non-empty `;`-separated segments.
  return trimmed
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean).length;
}

/**
 * Codex P2 (#417) — True when `property` is a KNOWN-NUMERIC mapped grid label for
 * `entityType` (its typed schema field is `z.ZodNumber`), bounded OR unbounded.
 *
 * Replaces the old shell-local `numericLabelConstraints` map (which only knew the
 * RANGE-bounded labels — MText Height/Width, Text Height — and so let an UNBOUNDED
 * numeric field like MText `Rotation`/`Line spacing` or Multileader `Leader type`
 * slip past the shell's numeric gate while the document OMITTED it). The
 * classification is now derived from the SAME schema-driven `numericFieldsByType`
 * the document uses (via the shared `classifyNumericLabel`), so the two sides agree
 * on EVERY numeric field, not just the bounded ones.
 */
function isConstrainedNumericLabel(
  entityType: string,
  property: string,
): boolean {
  return classifyNumericLabel(entityType, property) !== null;
}

function isWellFormedCoordinateProperty(
  property: string,
  value: string,
  currentValue: string | undefined,
  // Codex P2-1 (#317·round-6) — the entity's vertex count, threaded so the
  // `Bulges` branch can reject a finite-but-wrong-length bulge list (AutoCAD
  // stores exactly one bulge per vertex). Undefined ⇒ count unknown (no
  // count-lock, only finiteness), mirroring the document translator's fallback.
  vertexCount?: number,
  // Codex P2 (#417) — the entity TYPE, threaded so the numeric-bound gate below
  // can disambiguate same-label different-bound fields (MText Height positive vs
  // Text Height nonnegative) and match the document translator's reject.
  entityType?: string,
): boolean {
  // Codex P2 (#417) — numeric finiteness + RANGE bound for EVERY mapped numeric
  // grid label (bounded MText Height/Width, Text Height AND unbounded MText
  // Rotation/Line spacing, Multileader Leader type, …). Uses the SHARED
  // `classifyNumericLabel` (schema-derived numeric field + its range bound) and
  // the SHARED `parseFiniteNumeric` grammar / `satisfiesNumericConstraint` check,
  // so the shell accepts/rejects the EXACT same set as the document translator:
  //   • `parseFiniteNumeric` accepts `" 5 "` / `".5"` / `"1e3"`, rejects `""` /
  //     `"abc"` (null) — replacing the old `Number(value)` + narrow-grammar split
  //     that diverged from the document's regex.
  //   • the range bound rejects an out-of-bounds value (Height=0, Width=-5,
  //     Text Height=-1) exactly as the document OMITS it.
  // Other (non-numeric, coordinate/knot) labels fall through unchanged.
  if (entityType !== undefined) {
    const numeric = classifyNumericLabel(entityType, property);
    if (numeric !== null) {
      const n = parseFiniteNumeric(value);
      if (n === null) return false;
      if (!satisfiesNumericConstraint(numeric.field, n)) return false;
      return true;
    }
    // @codex #487 P2 — a mapped ENUM grid label (Revision Cloud `Creation mode`
    // → `z.enum` `creationMode`) must reject a value OUTSIDE its schema domain so
    // the SHELL preview never keeps a value the DOCUMENT translator OMITS. Uses
    // the SHARED schema-derived `classifyEnumLabel` (enum field + allowed domain)
    // — the SAME one truth the document consults via `enumFieldsByType` — so a
    // direct `properties/apply` with `Creation mode: "Freehand"` is dropped on
    // BOTH sides in lockstep (shell↔document parity). A value IN the domain is
    // accepted; other (non-numeric, non-enum, coordinate/knot) labels fall
    // through unchanged.
    const enumClass = classifyEnumLabel(entityType, property);
    if (enumClass !== null) {
      return enumClass.allowed.has(value);
    }
    // @codex #487 P2 — a mapped string-DOMAIN grid label (Revision Cloud `Cloud
    // style` → `revisionCloudStyle`, a `z.string()` with a FIXED Normal /
    // Calligraphy domain the schema does NOT encode as a `z.enum`). Reject a
    // value outside that domain so the SHELL preview never keeps a `"Foo"` /
    // numeric-looking `"1"` the DOCUMENT translator OMITS. Uses the SHARED
    // `classifyStringDomainLabel` — the SAME one truth (`stringDomainFieldsByType`)
    // the document consults — so a direct `properties/apply` with an unsupported
    // Cloud style is dropped on BOTH sides in lockstep (shell↔document parity). A
    // value IN the domain is accepted; other labels fall through unchanged.
    const stringDomainClass = classifyStringDomainLabel(entityType, property);
    if (stringDomainClass !== null) {
      return stringDomainClass.allowed.has(value);
    }
  }
  // Comma-separated finite-number list (SPLINE knot vector). An empty string
  // is a valid (empty) knot list.
  if (property === "Knots") {
    const trimmed = value.trim();
    if (trimmed === "") return true;
    // Codex P2-1 (#317) — reject an EMPTY token before coercion. `Number("")`
    // is 0 (finite), so a blank entry (`"0,,1"` or a trailing `"0,1,"`) would
    // otherwise slip through as a spurious 0 knot. Every comma-separated token
    // must be a non-empty finite number.
    return trimmed.split(",").every((tok) => {
      const t = tok.trim();
      return t !== "" && Number.isFinite(Number(t));
    });
  }
  // Codex P2-2 (#317) — per-vertex bulge list (LWPOLYLINE `Bulges`). Mirror the
  // STRICT document parser — since S15 Slice A that is the typed `Bulges`
  // descriptor `toRaw` (`descriptors/curves.ts`, path `vertices[].bulge`,
  // count-locked `/[;,]/` parse; the legacy `parseBulgeListStrict` it replaced
  // is DELETED): every token must be a non-empty finite number so a malformed
  // `Bulges` edit (`"0.5;oops;1"` / `"0.5;;1"`) is rejected on BOTH the shell
  // and the document (lockstep — the descriptor toRaw returns the reject
  // sentinel and no patch lands).
  // An empty string is NOT a valid bulge list (a polyline always has bulges).
  if (property === "Bulges") {
    const tokens = value.split(/[;,]/).map((s) => s.trim());
    if (tokens.length === 0) return false;
    if (!tokens.every((t) => t !== "" && Number.isFinite(Number(t)))) {
      return false;
    }
    // Codex P2-1 (#317·round-6) — COUNT-LOCK against the entity's vertex count
    // so a finite-but-wrong-length bulge list (`"0.5;0"` on a 3-vertex
    // polyline) is rejected on the SHELL too. Without this the document side
    // (S15: the typed `Bulges` descriptor toRaw, count == vertexCount)
    // rejected the edit while the shell preview kept the mutated value →
    // shell/doc divergence. When the vertex count is unknown (no Vertices
    // property threaded) we skip the lock and rely on the document side's own
    // fallback.
    if (vertexCount !== undefined && tokens.length !== vertexCount) {
      return false;
    }
    return true;
  }
  // Coordinate-pair / triple list. LWPOLYLINE `Vertices` are 2-D (exactly 2
  // fields); SPLINE `Control points` / `Fit points` may be 2-D (grip drag) OR
  // 3-D (raw inverse snapshot carrying Z) → accept 2 OR 3 fields. Every
  // segment must be fully finite or the whole string is rejected.
  if (
    property === "Vertices" ||
    property === "Control points" ||
    property === "Fit points"
  ) {
    const allowTriple = property !== "Vertices";
    const segments = value
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    for (const seg of segments) {
      // Codex P2-1 (#317) — reject an EMPTY coordinate token BEFORE coercion.
      // `Number("")` is 0 (finite), so a missing field (`"10,"`, `"1,,2"`)
      // previously slipped past as a spurious 0 coordinate / Z and silently
      // moved or flattened LWPOLYLINE/SPLINE geometry on a typo. Each token
      // must be a NON-EMPTY finite number, and the segment must have exactly
      // 2 (or 2/3) such tokens — a single bad token rejects the whole string.
      const tokens = seg.split(",").map((n) => n.trim());
      const arityOk = allowTriple
        ? tokens.length === 2 || tokens.length === 3
        : tokens.length === 2;
      if (
        !arityOk ||
        !tokens.every((t) => t !== "" && Number.isFinite(Number(t)))
      ) {
        return false;
      }
    }
    // LWPOLYLINE `Vertices` is a fixed-count grip / panel edit — the document
    // translator count-locks it (a vertex move never adds / removes a vertex),
    // so reject a count mismatch here too (shell must agree with document).
    // SPLINE `Control points` / `Fit points` are NOT count-locked: an undo of
    // a fit-grip recompute legitimately restores a different control-point
    // count, so they skip this check (mirrors the document translator).
    if (property === "Vertices" && currentValue !== undefined) {
      const currentCount = currentValue
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean).length;
      if (currentCount > 0 && segments.length !== currentCount) {
        return false;
      }
    }
    return true;
  }
  // Any other property is not coordinate-validated here.
  return true;
}

export function applyExtendedEntityPropertyChanges(
  entity: EntityPreview,
  changes: SimpleGeometryPropertyChange[],
): ExtendedPropertyChangeResult {
  const entityType = entity.type;
  const patchSet = extendedPatchableLookup[entityType];

  if (!patchSet) {
    return {
      entity,
      patch: null,
      appliedChanges: [],
      skipped: changes.map((c) => ({
        entityId: entity.id,
        property: c.name,
        reason: `unsupported extended entity type: ${entityType}`,
        kind: "incompatible" as const,
      })),
      summary: `Extended property changes: unsupported entity type ${entityType}`,
    };
  }

  const appliedChanges: SimpleGeometryPropertyChange[] = [];
  const skipped: SimpleGeometryPropertySkip[] = [];
  const nextProperties = { ...entity.properties };
  let anyChanged = false;

  for (const change of changes) {
    if (!patchSet.has(change.name)) {
      // fix/properties-apply-derived-fields-skip — a row like LWPOLYLINE
      // Length/Area or Dimension Measurement is resubmitted unedited on
      // EVERY Properties-panel apply (the panel drafts the whole row set,
      // not just the touched field); classify it "derived" so it never
      // inflates the "skipped N incompatible" audit count. A property that
      // genuinely does not belong to this entity type stays "incompatible",
      // counted exactly as before.
      const derived = isDerivedDisplayProperty(entityType, change.name);
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason: derived
          ? `property is read-only or not patchable for ${entityType}`
          : `${change.name} does not apply to ${entityType}`,
        kind: derived ? "derived" : "incompatible",
      });
      continue;
    }

    if (nextProperties[change.name] === change.value) {
      continue;
    }

    // Codex P2-5 (#317) — reject a malformed coordinate / knot edit so the
    // shell preview is NOT mutated when the document translator would reject
    // it (all-or-nothing across BOTH). Skipping leaves nextProperties intact.
    if (
      !isWellFormedCoordinateProperty(
        change.name,
        change.value,
        entity.properties[change.name],
        // Codex P2-1 (#317·round-6) — vertex count for the `Bulges` count-lock,
        // derived from the live preview `Vertices` property (coordinate string
        // or bare count). Other properties ignore it.
        lwpolylineVertexCount(entity.properties["Vertices"]),
        // Codex P2 (#417) — entity type for the MText/Text Height/Width numeric
        // bound gate (same label, different bound per type).
        entityType,
      )
    ) {
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason: isConstrainedNumericLabel(entityType, change.name)
          ? `out-of-range or non-numeric value for ${change.name}; edit rejected (shell↔document parity)`
          : classifyEnumLabel(entityType, change.name) !== null
            ? `value outside the ${change.name} enum domain; edit rejected (shell↔document parity)`
            : classifyStringDomainLabel(entityType, change.name) !== null
              ? `value outside the ${change.name} supported domain; edit rejected (shell↔document parity)`
              : `malformed coordinate / knot value for ${change.name}; edit rejected (all-or-nothing)`,
        // A VALUE-validation failure on an already-patchable property — a
        // genuine incompatibility (the property IS settable; this submitted
        // value was rejected), never a derived-field matter.
        kind: "incompatible",
      });
      continue;
    }

    // S15 Slice A (@codex P2 ×2 on c8394b70) — mirror the typed writer's
    // descriptor verdict for every writer-OWNED label (registry-derived set —
    // `getTypedExtendedGeometryOwnedLabels` — so future cutover slices inherit
    // this gate by construction). The mirror resolves the SAME registry
    // descriptor and runs the SAME `parseTypedPropertyInput` core the document
    // write (geometryReducer Step 1d) runs — never a hand-copied min/int rule.
    // Without it the shell copied e.g. ArrayRect Rows="0" (min:1+int) or
    // LWPOLYLINE Constant width="-1" (min:0) into the preview bag while the
    // typed document REJECTED the same edit → the Properties grid / audit
    // showed a value the DrawingDocument (and DXF export) never had. Runs
    // AFTER the coordinate/knot validator above so the malformed-list family
    // keeps its specific audit reasons; anything the writer rejects beyond
    // that family (numeric constraints, future owned-label shapes) skips here.
    // Pinned by extendedPropertyMutations.shellMirror.census.test.ts
    // (shell-accepts ⊆ writer-accepts over the full owned-label matrix).
    // N8 (@codex P2 round 2) — thread the preview bag so a descriptor's
    // `readOnlyFromBag` companion can see a SIBLING field's value (Hatch
    // `Double` ← "Pattern type") instead of nothing at all.
    // @codex P2 round 5 — ALSO thread the full raw `changes` array so a
    // descriptor can scan it order-independently FIRST (Hatch `Double`
    // resolves a "Pattern type" SIBLING CHANGE anywhere in the batch, not
    // only one the loop has already applied) — the shell and the document-
    // side `readOnlyFromChanges` (which always scans the whole batch) now
    // resolve the IDENTICAL verdict regardless of which order the caller
    // lists the fields in.
    // @codex P2 round 7 — the bag parameter is the PRE-APPLY `entity.
    // properties`, NOT the within-apply `nextProperties` accumulator.
    // `nextProperties` can itself be CORRUPTED by the very sibling change a
    // descriptor is trying to fall back on: "Pattern type" is NOT typed-
    // writer-owned (an enum, validated only by the separate legacy
    // translator, never by this shell's format gate), so a MALFORMED
    // "Pattern type" entry earlier in `changes` sails straight into
    // `nextProperties["Pattern type"]` verbatim — reading THAT back as the
    // "current state" fallback would see the malformed string, not the
    // entity's real prior value. `entity.properties` is immutable
    // throughout this function and reflects exactly what was true BEFORE
    // this batch started — the same state the real document keeps when the
    // malformed submit is silently dropped. This does not reintroduce
    // round-5's order-dependency: a VALID sibling change is ALREADY
    // resolved by the `changes` array scan above, which never consults the
    // bag parameter at all. Every other descriptor ignores both extra
    // arguments.
    if (
      typedExtendedGeometryShellRejects(
        entityType,
        change.name,
        change.value,
        entity.properties,
        changes,
      )
    ) {
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason:
          `value rejected by the typed ${change.name} descriptor constraint; ` +
          `edit rejected (shell↔document parity)`,
        kind: "incompatible",
      });
      continue;
    }

    // S15 Slice G (@codex P2 — canonicalizing-no-op display↔document desync) — a
    // CANONICALIZING input (e.g. MINSERT "Column count"="2.4" on a block already
    // at 2 → the `int` descriptor rounds to raw 2 == current) is a NO-OP on the
    // typed DOCUMENT (geometryReducer Step 1d → the writer's rawEqual backstop),
    // so storing the RAW submitted "2.4" here would leave the Properties panel
    // showing a value the document / DXF export never persist (the deleted legacy
    // Block branch always `Math.round`-ed it back). KEEP the current canonical
    // display instead — mirrors the writer's no-op through the SAME
    // `parseTypedPropertyInput` gate (one truth). A display-equal re-submit was
    // already skipped above; a full-precision re-type that parses to a different
    // raw is NOT canonicalizing and stays on the store path (the #317/#418
    // precision-preserve no-op is untouched). Pinned by
    // extGeomSliceG.canonicalizeDesync.store.test.ts + the shell-mirror census.
    if (
      typedExtendedGeometryShellCanonicalizes(
        entityType,
        change.name,
        change.value,
        entity.properties[change.name],
      )
    ) {
      continue;
    }

    nextProperties[change.name] = change.value;
    appliedChanges.push(change);
    anyChanged = true;
  }

  if (!anyChanged) {
    return {
      entity,
      patch: null,
      appliedChanges,
      skipped,
      summary: `Extended property changes for ${entityType}: no applicable changes`,
    };
  }

  /* Recompute computed properties where applicable */
  if (entityType === "LWPOLYLINE") {
    // NOTE: "Vertices" may be EITHER a count (command-engine's own
    // preview builder) OR an "x,y;x,y" coordinate string (the app's
    // dxfGeometry.buildLwpolylinePreview + GRIPS-SUBSYSTEM vertex drag).
    // Length / Area are only recomputed from real coordinates elsewhere;
    // here we only flag the closed-state recompute, which is independent
    // of the vertex representation.
    // Tolerant parse — canonical "true"/"false" AND legacy "Yes"/"No"/"1"/"0"
    // must both round-trip through the property panel without changing
    // computed Length / Area. See codex P2 review on PR #57. The rule itself
    // is `geometryEntityTypes.ts::parseClosedFlagProperty`, the ONE reading of
    // this bag row, shared with the MVIEW-FIT extent walk (`./extent.ts`).
    const closed = parseClosedFlagProperty(nextProperties["Closed"]);
    /* Computed length and area can only be fully recalculated
       with actual vertex coordinates; for property-panel editing
       we preserve the existing computed values unless vertices change */
    if (nextProperties["Closed"] !== entity.properties["Closed"]) {
      /* Mark computed values stale by keeping them but note
         they require geometry recalculation */
      nextProperties["Length"] = nextProperties["Length"] ?? "0";
      nextProperties["Area"] = closed ? (nextProperties["Area"] ?? "0") : "0";
    }
  }

  if (entityType === "Dimension") {
    const ext1x = parseNumber(nextProperties["Extension 1 X"], 0);
    const ext1y = parseNumber(nextProperties["Extension 1 Y"], 0);
    const ext2x = parseNumber(nextProperties["Extension 2 X"], 0);
    const ext2y = parseNumber(nextProperties["Extension 2 Y"], 0);
    const measurement = computeDimensionMeasurement(
      { x: ext1x, y: ext1y },
      { x: ext2x, y: ext2y },
    );
    nextProperties["Measurement"] = formatNumber(measurement);
  }

  const changesRecord: Record<string, unknown> = {};
  for (const change of appliedChanges) {
    changesRecord[change.name] = change.value;
  }

  const updatedEntity: EntityPreview = {
    ...entity,
    properties: nextProperties,
  };

  return {
    entity: updatedEntity,
    patch: {
      entityId: entity.id,
      type: entityType as ExtendedGeometryPatch["type"],
      changes: changesRecord,
    },
    appliedChanges,
    skipped,
    summary: `Extended property changes for ${entityType}: ${appliedChanges.length} property(s) updated`,
  };
}
